import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Detail, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { useClientOptions } from './Invoices';

const BACK = '/finance/migration';

function useMigrationRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['migration-status'] });
    void queryClient.invalidateQueries({ queryKey: ['migration-invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['migration-payments'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['payments'] });
  };
}

/**
 * CR-001 §50 — give a quarantined invoice the client it really belonged to.
 * Route: `/finance/migration/invoices/:id/restore`.
 */
export function RestoreInvoicePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useMigrationRefresh();
  const clients = useClientOptions();
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');

  const rows = useQuery({
    queryKey: ['migration-invoices'],
    queryFn: () => api.get<any[]>('/finance/migration/unlinked-invoices'),
  });
  const row = (rows.data ?? []).find((r) => r.id === id);

  const projects = useQuery({
    queryKey: ['project-options', clientId],
    queryFn: () => api.get<any[]>('/projects/options', { clientId }),
    enabled: !!clientId,
  });

  const restore = useMutation({
    mutationFn: () =>
      api.post(`/finance/migration/unlinked-invoices/${id}/restore`, {
        clientId,
        projectId: projectId || undefined,
      }),
    onSuccess: () => {
      refresh();
      navigate(BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('migration.restoreInvoice')}
      backTo={BACK}
      backLabel={t('migration.title')}
      submitLabel={t('migration.restore')}
      submitting={restore.isPending}
      disabled={!clientId}
      loading={rows.isLoading}
      error={rows.error}
      onRetry={() => void rows.refetch()}
      onSubmit={() => restore.mutate()}
      summary={
        row && (
          <>
            <dl className="grid gap-x-6 sm:grid-cols-2">
              <Detail label={t('tickets.number')}>
                <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{row.number}</span>
              </Detail>
              <Detail label={t('invoices.counterparty')}>{row.counterpartyName}</Detail>
              <Detail label={t('invoices.issueDate')}>{formatDate(row.issueDate, locale)}</Detail>
              <Detail label={t('common.total')}>{formatMoney(row.totalAmount, row.currency, locale)}</Detail>
            </dl>
            <p className="mt-3 text-caption text-muted">{row.reason}</p>
          </>
        )
      }
    >
      <FormGrid>
        <Field label={t('invoices.client')} required>
          <Select
            value={clientId}
            onChange={(e) => { setClientId(e.target.value); setProjectId(''); }}
            autoFocus
          >
            <option value="">—</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('invoices.project')}
          hint={clientId ? t('invoices.projectHint') : t('invoices.pickClientFirst')}
        >
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId}>
            <option value="">{t('invoices.noProject')}</option>
            {(projects.data ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>{p.code} — {localized(p)}</option>
            ))}
          </Select>
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * §51 — put a quarantined payment back against a real invoice, with the rate
 * that applied on its date. Route: `/finance/migration/payments/:id/restore`.
 */
export function RestorePaymentPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useMigrationRefresh();
  const [invoiceId, setInvoiceId] = useState('');
  const [exchangeRate, setExchangeRate] = useState<number | ''>('');

  const rows = useQuery({
    queryKey: ['migration-payments'],
    queryFn: () => api.get<any[]>('/finance/migration/unlinked-payments'),
  });
  const row = (rows.data ?? []).find((r) => r.id === id);

  // Only invoices in the payment's own currency with a balance owing can settle
  // it — anything else the server would reject anyway.
  const invoices = useQuery({
    queryKey: ['migration-target-invoices', row?.currency],
    queryFn: () => api.get<any>('/invoices', { pageSize: 200, currency: row.currency, outstandingOnly: 'true' }),
    enabled: !!row,
  });

  const restore = useMutation({
    mutationFn: () =>
      api.post(`/finance/migration/unlinked-payments/${id}/restore`, {
        invoiceId,
        exchangeRate: exchangeRate === '' ? undefined : Number(exchangeRate),
      }),
    onSuccess: () => {
      refresh();
      navigate(BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('migration.restorePayment')}
      backTo={BACK}
      backLabel={t('migration.title')}
      submitLabel={t('migration.restore')}
      submitting={restore.isPending}
      disabled={!invoiceId}
      loading={rows.isLoading}
      error={rows.error}
      onRetry={() => void rows.refetch()}
      onSubmit={() => restore.mutate()}
      summary={
        row && (
          <>
            <dl className="grid gap-x-6 sm:grid-cols-2">
              <Detail label={t('common.date')}>{formatDate(row.paymentDate, locale)}</Detail>
              <Detail label={t('common.amount')}>{formatMoney(row.amount, row.currency, locale)}</Detail>
              <Detail label={t('payments.method')}>{row.paymentMethod.replace('_', ' ')}</Detail>
              <Detail label={t('migration.originalInvoice')}>{row.originalInvoiceNumber ?? '—'}</Detail>
            </dl>
            <p className="mt-3 text-caption text-muted">{row.reason}</p>
          </>
        )
      }
    >
      <FormGrid>
        <Field label={t('migration.pickInvoice')} required className="sm:col-span-2">
          <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} autoFocus>
            <option value="">—</option>
            {(invoices.data?.items ?? []).map((inv: any) => (
              <option key={inv.id} value={inv.id}>
                {inv.number} — {inv.client.companyName} ({formatMoney(inv.remainingAmount, inv.currency, locale)})
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('payments.exchangeRate')} hint={t('payments.rateHint')}>
          <Input
            type="number"
            step="0.00000001"
            min="0"
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * Writing a quarantined row off instead of restoring it — a decision with a
 * name and a reason on it, recorded in the audit log.
 *
 * Routes: `/finance/migration/{invoices|payments}/:id/discard`.
 */
export function DiscardMigrationRowPage({ kind }: { kind: 'invoice' | 'payment' }) {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useMigrationRefresh();
  const [reason, setReason] = useState('');

  const listKey = kind === 'invoice' ? 'migration-invoices' : 'migration-payments';
  const path = kind === 'invoice' ? 'unlinked-invoices' : 'unlinked-payments';

  const rows = useQuery({
    queryKey: [listKey],
    queryFn: () => api.get<any[]>(`/finance/migration/${path}`),
  });
  const row = (rows.data ?? []).find((r) => r.id === id);

  const discard = useMutation({
    mutationFn: () => api.post(`/finance/migration/${path}/${id}/discard`, { reason }),
    onSuccess: () => {
      refresh();
      navigate(BACK);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('migration.discard')}
      backTo={BACK}
      backLabel={t('migration.title')}
      submitLabel={t('common.confirm')}
      destructive
      submitting={discard.isPending}
      disabled={!reason.trim()}
      loading={rows.isLoading}
      error={rows.error}
      onRetry={() => void rows.refetch()}
      onSubmit={() => discard.mutate()}
      summary={
        row && (
          <>
            <dl className="grid gap-x-6 sm:grid-cols-2">
              {kind === 'invoice' ? (
                <>
                  <Detail label={t('tickets.number')}>
                    <span className="font-mono text-mono-sm">{row.number}</span>
                  </Detail>
                  <Detail label={t('invoices.counterparty')}>{row.counterpartyName}</Detail>
                </>
              ) : (
                <>
                  <Detail label={t('common.date')}>{formatDate(row.paymentDate, locale)}</Detail>
                  <Detail label={t('migration.originalInvoice')}>{row.originalInvoiceNumber ?? '—'}</Detail>
                </>
              )}
              <Detail label={t('common.amount')}>
                {formatMoney(kind === 'invoice' ? row.totalAmount : row.amount, row.currency, locale)}
              </Detail>
            </dl>
            <p className="mt-3 text-caption text-muted">{row.reason}</p>
          </>
        )
      }
    >
      <Field label={t('migration.discardReason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
